import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useProducts } from "./product-step/useProducts";
import { ProductLoadingSkeleton } from "./ProductLoadingSkeleton";
import { ProductSelectionContent } from "./LazyComponents";
import { ExpandableOrderSummary } from "./booking/ExpandableOrderSummary";
import { PackageTypeToggle } from "./product-step/PackageTypeToggle";
import { Product } from "./product-step/types";
import { useClinics } from "@/hooks/useClinics";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import {
  getMarketingPrefix,
  isPackageTypeHiddenInMarket,
  marketHasMembership,
} from "@/config/market";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useBookingStore } from "@/stores/bookingStore";
import { languageMap } from "@/i18n";
import { useMembership } from "@/contexts/MembershipContext";
import { isFollowupBloodTest } from "@/utils/bloodTestVisibility";
import { MembershipCard } from "./product-step/MembershipCard";
import { BloodTrackingLayout } from "./product-step/BloodTrackingLayout";
import { pickBaselineProduct } from "./product-step/pickBaselineProduct";
import { buttonVariants, NudgeArrow } from "@/components/ui/ds-button";
import { trackMembershipCtaClick } from "@/utils/analytics";
import {
  membershipSignupUrl,
  prefetchMembershipPages,
} from "@/utils/prefetchMembership";

// This component now uses the bookingFlowStore directly
const ProductStep: React.FC = () => {
  const {
    language,
    selectedPackageType,
    setSelectedPackageType,
    selectedProducts,
    selectedAddOns,
    selectedPackageId,
    setSelectedPackageId,
    selectProducts,
    addProduct,
    removeProduct,
    toggleAddOn,
    goToStep,
    goBack,
    canGoBack,
    canProceed,
    selectedClinic,
    selectClinic,
    selectedTimeSlots,
    selectedProductDetails,
    selectedAddOnDetails,
    initializeFromUrl,
    initializeTestModeNavigation,
  } = useBookingFlow();

  const { market, forcedPackageTypes } = useMarket();
  const { data: featureFlags } = useFeatureFlags();
  const membershipFlowEnabled =
    marketHasMembership(market) && (featureFlags?.["membership_v2"] ?? true);
  const { data, isLoading, error, refetch } = useProducts(language, market);
  const { data: clinics } = useClinics();
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const {
    isMember,
    isLoading: isMembershipLoading,
    getEffectivePrice,
    showAllBloodTestsFlag,
  } = useMembership();

  // First pass: choosing "Become a member" reuses the add-ons step chrome (real
  // sidebar + order summary) to show the membership cadence page. We select the
  // blood-test package so the order summary populates, flip the flag, and go to
  // the dedicated cadence step.
  const setMembershipPlanActive = useMembershipPlanStore(
    (state) => state.setActive,
  );
  const setMembershipPlan = useMembershipPlanStore((state) => state.setPlan);
  const bundleAttached = useMembershipPlanStore(
    (state) => state.bundleAttached,
  );
  const setBundleAttached = useMembershipPlanStore(
    (state) => state.setBundleAttached,
  );
  const setMembershipFirst = useMembershipPlanStore(
    (state) => state.setMembershipFirst,
  );

  const hasInitializedFromUrl = useBookingFlowStore(
    (state) => state.hasInitializedFromUrl,
  );

  useEffect(() => {
    // When a membership token is present, wait for it to resolve before initializing
    // so the blood-only default tab can be set correctly
    if (isMembershipLoading) return;
    if (data?.products && data.products.length > 0) {
      initializeFromUrl(data.products);
    }
  }, [data?.products, initializeFromUrl, isMembershipLoading]);

  useEffect(() => {
    const locale = languageMap[language] ?? "en";
    prefetchMembershipPages(locale);
  }, [language]);

  // Clear the membership cadence flag whenever the product step is shown, so
  // returning here (or starting fresh) never leaves the add-ons step showing
  // the cadence instead of real add-ons.
  //
  // Gated on `hasInitializedFromUrl`, NOT on reading ?packageType=membership:
  // the thing to skip is the transient mount BEFORE the store has initialized
  // (a ?packageType=membership entry renders the product step for a beat on its
  // way to cadence, and clearing there would undo the entry the store is
  // setting up). The URL param is a bad proxy for that moment — nothing ever
  // strips it, so it reads true on every LATER visit too, and the reset was
  // then skipped for the whole session: backing out of the cadence flow and
  // picking the one-off test still checked out as a recurring membership.
  // `hasInitializedFromUrl` flips exactly once, in the same set() that moves
  // the step to `cadence`, so the transient mount is the only one it suppresses.
  useEffect(() => {
    if (!hasInitializedFromUrl) return;
    // Returning to the product step normally clears the cadence flag so the
    // add-ons step never shows the cadence instead of add-ons. But a deliberate
    // scan+membership bundle must survive a back-navigation.
    if (bundleAttached) return;
    setMembershipPlanActive(false);
    setMembershipFirst(false);
  }, [
    hasInitializedFromUrl,
    setMembershipPlanActive,
    setMembershipFirst,
    bundleAttached,
  ]);

  // When a member arrives without an explicit packageType URL param, default to
  // blood tests — but only once on load, so the user can freely switch tabs.
  const hasDefaultedToBloodOnly = useRef(false);
  useEffect(() => {
    if (isMembershipLoading) return;
    if (!isMember) return;
    if (hasDefaultedToBloodOnly.current) return;
    const urlPackageType = new URLSearchParams(window.location.search).get(
      "packageType",
    );
    if (!urlPackageType) {
      hasDefaultedToBloodOnly.current = true;
      setSelectedPackageType("blood-only");
    }
  }, [isMember, isMembershipLoading, setSelectedPackageType]);

  useEffect(() => {
    if (hasInitializedFromUrl) {
      initializeTestModeNavigation();
    }
  }, [hasInitializedFromUrl, initializeTestModeNavigation]);

  // If the active package type isn't offered in the current market (e.g. the
  // default "mri" in Germany, which only offers the blood test), fall back to
  // the blood-only tab that remains visible.
  useEffect(() => {
    if (
      isPackageTypeHiddenInMarket(
        market,
        selectedPackageType,
        forcedPackageTypes,
      )
    ) {
      setSelectedPackageType("blood-only");
    }
  }, [market, selectedPackageType, forcedPackageTypes, setSelectedPackageType]);

  // Handlers
  const handlePackageSelect = (packageId: string) => {
    console.log("[ProductStep] Package selected:", packageId);
    setSelectedPackageId(packageId);
    selectProducts([packageId], { skipAddons: false });
  };

  const handleSelectPackageAndContinue = (packageId: string) => {
    // Picking a package outright starts a package-led cart, so clear any
    // membership entry state first. It persists for the session, and the
    // mount-time reset above can't be relied on — its deps never change, so
    // navigating back to this step doesn't re-run it. Without this, anyone
    // who once went blood → "Become a member" keeps the membership, the
    // member prices and the scan-upsell section on every later package pick
    // (AHD-8140, raised by Nick).
    setMembershipFirst(false);
    setMembershipPlanActive(false);
    setBundleAttached(false);
    handlePackageSelect(packageId);
    // This is for modals/CTAs where user explicitly wants to select and continue
    // Wait a bit for state to update, then navigate
    setTimeout(() => {
      goToStep("addons");
    }, 100);
  };

  /** "Book as a member" on a scan card: same pick, but the membership rides
   *  along. `membershipFirst` stays false — they entered via the package, so
   *  the add-ons step must not offer them a scan they already chose. */
  const handleSelectPackageAsMember = (packageId: string) => {
    setMembershipFirst(false);
    setMembershipPlan("baseline");
    setMembershipPlanActive(true);
    setBundleAttached(true);
    handlePackageSelect(packageId);
    setTimeout(() => {
      goToStep("addons");
    }, 100);
  };

  const handleProductToggle = (productId: string) => {
    console.log("[ProductStep] Product toggled:", productId);
    if (selectedProducts.includes(productId)) {
      removeProduct(productId);
    } else {
      addProduct(productId);
    }
  };

  const handleAddOnToggle = (addOnId: string) => {
    console.log("[ProductStep] Add-on toggled:", addOnId);
    toggleAddOn(addOnId);
  };

  const handleContinue = () => {
    console.log("[ProductStep] Continue clicked");
    // Store's goToStep handles validation automatically
    goToStep("addons");
  };

  const handleStartMembership = () => {
    // Belt-and-braces: the entry point is only wired when the feature is on
    // (CH + flag), but guard here too so the cadence path can never flip on.
    if (!membershipFlowEnabled) return;
    // Select the baseline blood-test package so the reused order summary /
    // sidebar populate, flag membership mode (drives the indicator, sidebar and
    // active step order), then enter the real, dedicated cadence step.
    const baseline = pickBaselineProduct(filteredProducts);
    if (baseline) handlePackageSelect(baseline.id);
    // Default cadence is always baseline until the user picks otherwise in the
    // cadence step — avoids a stale in-session selection confusing them later.
    setMembershipPlan("baseline");
    setMembershipPlanActive(true);
    // Signal membership-first direction so the add/remove upsell card on the
    // add-ons step is hidden (it's only meaningful in the MRI-first path).
    setMembershipFirst(true);
    goToStep("cadence", { skipValidation: true });
  };

  const handlePackageTypeToggle = (packageType: string) => {
    // Ignore empty value or same button re-click (prevent clearing selection)
    if (!packageType || packageType === selectedPackageType) {
      console.log(
        "[ProductStep] Same package type clicked, ignoring:",
        packageType,
      );
      return;
    }
    console.log("[ProductStep] Package type toggled:", packageType);
    setSelectedPackageType(packageType);
  };

  // Get inferred package type from store
  const inferredPackageType = useMemo(() => {
    return useBookingFlowStore.getState().getInferredPackageType();
  }, [selectedProducts, selectedPackageType, data?.products]);

  // Filter products by selected package type
  const filteredProducts = useMemo(() => {
    if (!data?.products) return [];
    return data.products.filter((product) => {
      // Check if product has package_types defined
      const supportedTypes = product.package_types || [];

      // If package_types is empty or undefined, show for all types (backward compatibility)
      if (supportedTypes.length === 0) {
        return true;
      }

      // Otherwise, only show if it supports the selected package type
      return supportedTypes.includes(selectedPackageType);
    });
  }, [data?.products, selectedPackageType]);

  // A deep-linked follow-up product (?products=<followup-id>) must land on the
  // follow-up screen (MembershipBloodTestSection), not the membership+one-off
  // comparison (BloodTrackingLayout) that non-members otherwise get.
  const isFollowupSelected = useMemo(
    () =>
      filteredProducts.some(
        (p) =>
          p.id === selectedPackageId &&
          isFollowupBloodTest(p.stripe_product_id),
      ),
    [filteredProducts, selectedPackageId],
  );

  // Filter clinics by package type
  const filteredClinics = useMemo(() => {
    if (!clinics) return [];
    return clinics.filter((clinic) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const acceptedTypes = (clinic as any).package_types || [
        "mri",
        "blood-only",
      ];
      return acceptedTypes.includes(selectedPackageType);
    });
  }, [clinics, selectedPackageType]);

  // Reset selected clinic if it's not in the filtered list
  // BUT only if clinics have actually loaded (filteredClinics.length > 0 or clinics loaded)
  useEffect(() => {
    // Don't reset if clinics haven't loaded yet - this prevents race condition with preselection
    if (!clinics || clinics.length === 0) {
      return;
    }

    if (
      selectedClinic &&
      !filteredClinics.find((c) => c.id === selectedClinic)
    ) {
      console.log(
        "[ProductStep] Selected clinic not available for package type, resetting",
      );
      selectClinic(null);
    }
  }, [
    selectedPackageType,
    selectedClinic,
    filteredClinics,
    selectClinic,
    clinics,
  ]);

  // Header translations using i18next
  const headerTranslations = {
    title: t("productStep.title"),
    description: t("productStep.description"),
  };

  // Only show the "Select your test" header when the market has more than one
  // selectable package type. Blood-only markets (NL/DE) have a single option, so
  // the header + subtitle are redundant alongside the (already hidden) lone pill.
  // `?force=mri` re-introduces a real choice, restoring the header.
  const hasPackageChoice =
    ["mri", "blood-only"].filter(
      (pt) => !isPackageTypeHiddenInMarket(market, pt, forcedPackageTypes),
    ).length > 1;

  // Order summary translations using i18next
  const orderSummaryTranslations = {
    orderSummary: t("orderSummary.title"),
    scan: t("orderSummary.scan"),
    addOns: t("orderSummary.addOns"),
    bloodTests: t("orderSummary.bloodTests"),
    location: t("orderSummary.location"),
    timeSlots: t("orderSummary.timeSlots"),
    totalPrice: t("orderSummary.totalPrice"),
    continue: t("common.next"),
    back: t("common.prev"),
    remove: t("common.remove"),
  };

  // Get selected clinic object
  const selectedClinicObject = selectedClinic
    ? clinics?.find((c) => c.id === selectedClinic)
    : null;

  // Calculate total price using member-effective prices so the collapsed
  // summary bar matches the per-item discounted prices shown above.
  const totalPrice = [
    ...selectedProductDetails,
    ...selectedAddOnDetails,
  ].reduce((sum, product) => sum + getEffectivePrice(product), 0);

  // For display/animation purposes
  const displayedPrice = totalPrice;
  const animatePrice = false;

  const hasSelectedProducts = selectedProductDetails.length > 0;

  // Don't show loading skeleton to avoid double skeleton flash
  // The main BookingSkeleton already handles initial loading
  // Just wait for products to load and show them directly
  if (isLoading) {
    return null;
  }

  // Show error state
  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 mb-4">{t("productStep.errorLoading")}</p>
        <Button onClick={() => window.location.reload()}>
          {t("common.reload")}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        {/* Header Section - hidden on mobile (redundant with step indicator), and
            hidden entirely when the market has no package-type choice (blood-only
            markets), where "Select your test" + a single pill is redundant. */}
        {hasPackageChoice && (
          <div className="text-center px-4 mb-6 hidden lg:block">
            <h1
              className="mb-2"
              style={{
                fontWeight: 400,
                color: "#2D2D2D",
                fontSize: 24,
              }}
            >
              {headerTranslations.title}
            </h1>
            <p
              className="mx-auto leading-relaxed"
              style={{ fontSize: 14, fontWeight: 400, color: "#2D2D2D" }}
            >
              {headerTranslations.description}
            </p>
          </div>
        )}

        <div className="space-y-6">
          {/* Member pricing banner — lives inside space-y-6 for consistent rhythm */}
          {isMember && !isMembershipLoading && (
            <div className="w-fit mx-auto flex items-center gap-2 bg-deep-green-100 rounded-full px-3 py-1.5">
              <svg
                className="shrink-0"
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.667 5L7.5 14.167 3.333 10"
                  stroke="#26542B"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-deep-green-700 text-sm">
                <span className="font-medium">
                  {t("membership.pricingBannerTitle")}
                </span>{" "}
                <span className="font-normal">
                  {t("membership.pricingBannerDesc")}
                </span>
              </p>
            </div>
          )}

          {/* Package Type Toggle */}
          <PackageTypeToggle
            selectedPackageType={selectedPackageType}
            onToggle={handlePackageTypeToggle}
            language={language}
            market={market}
            forcedPackageTypes={forcedPackageTypes}
          />

          {selectedPackageType === "membership" ? (
            <div className="space-y-10">
              <MembershipCard
                language={language}
                onStart={() => {
                  const locale = languageMap[language] ?? "en";
                  const href = membershipSignupUrl(locale);
                  trackMembershipCtaClick(
                    "become_a_member",
                    "membership_signup",
                    href,
                    locale,
                  );
                  window.open(href, "_blank");
                }}
              />
              <div className="flex justify-center">
                <a
                  href={`https://www.aheadhealth.com${getMarketingPrefix(market, language)}/membership`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) =>
                    trackMembershipCtaClick(
                      "learn_more",
                      "marketing_membership",
                      e.currentTarget.href,
                      languageMap[language] ?? "en",
                    )
                  }
                  className={`${buttonVariants({ variant: "outline" })} group pr-6`}
                >
                  {t(
                    "membershipCard.learnMore",
                    "Learn more about Ahead membership",
                  )}
                  <NudgeArrow />
                </a>
              </div>
            </div>
          ) : selectedPackageType === "blood-only" &&
            !isMember &&
            marketHasMembership(market) &&
            !showAllBloodTestsFlag &&
            !isFollowupSelected ? (
            /* Side-by-side "Single blood test" + recommended Ahead membership
               cards — replaces the standalone Advanced blood test card and the
               bottom membership upsell. Members skip this and fall through to
               ProductSelectionContent → PackageList → MembershipBloodTestSection.
               Blood-only markets (DE, NL) have no membership, so they also fall
               through to the plain blood-test layout. */
            <BloodTrackingLayout
              products={filteredProducts}
              selectedPackageId={selectedPackageId}
              onSelectPackage={handlePackageSelect}
              language={language}
              onSelectAndContinue={handleSelectPackageAndContinue}
              membershipTiers={data?.membershipTiers}
              onStartMembership={
                membershipFlowEnabled ? handleStartMembership : undefined
              }
            />
          ) : (
            <div className="space-y-6">
              <ProductSelectionContent
                products={filteredProducts}
                categories={data.categories}
                selectedPackageId={selectedPackageId}
                onSelectPackage={handlePackageSelect}
                localSelectedProducts={selectedProducts}
                onProductToggle={handleProductToggle}
                selectedAddOns={selectedAddOns}
                onAddOnToggle={handleAddOnToggle}
                displayedPrice={displayedPrice}
                animatePrice={animatePrice}
                language={language}
                onSelectPackageAndContinue={handleSelectPackageAndContinue}
                onSelectPackageAsMember={handleSelectPackageAsMember}
              />
            </div>
          )}
        </div>
      </div>

      {/* Expandable Order Summary Footer */}
      <ExpandableOrderSummary
        totalPrice={totalPrice}
        selectedProducts={selectedProductDetails}
        selectedAddOns={selectedAddOnDetails}
        selectedClinic={selectedClinicObject}
        selectedTimeSlots={selectedTimeSlots}
        language={language}
        onContinue={canProceed ? handleContinue : undefined}
        onBack={goBack}
        showBackButton={true}
        showContinueButton={true}
        continueDisabled={!canProceed}
        backDisabled={!canGoBack}
        onProductRemove={handleProductToggle}
        onAddOnRemove={handleAddOnToggle}
        onEditProducts={() => goToStep("product", { skipValidation: true })}
        onEditAddOns={() => goToStep("addons", { skipValidation: true })}
        onEditLocation={() => goToStep("clinic", { skipValidation: true })}
        onEditTimeSlots={() => goToStep("date", { skipValidation: true })}
        translations={orderSummaryTranslations}
        currentStep="product"
      />
    </>
  );
};

export default ProductStep;
